import { useQuery } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { CONNECT_STATUS_KEY, fetchConnectStatus } from '@/api/connect';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';
import { setupInProgress, useSetup } from '@/hooks/useSetup';

/**
 * The nudge to connect Stripe, across the top of every signed-in screen.
 *
 * Online payments are the kind of feature nobody discovers by browsing a
 * settings page, so the app says it once, everywhere, until it is true. It
 * disappears the moment the team can actually take a payment.
 *
 * There is no close button on purpose. The only way to dismiss it is the
 * switch in Invoice Settings, which is also where the thing it is asking for
 * lives — so the two ways out of the banner are "set it up" and "decide you
 * don't want it", both of which land on the same screen.
 */
export function PaymentsSetupBanner() {
  const { pathname } = useLocation();
  const { isAdmin } = useSession();

  const { data } = useQuery({
    queryKey: CONNECT_STATUS_KEY,
    queryFn: ({ signal }) => fetchConnectStatus(signal),
    staleTime: 60_000
  });

  // While the setup guide is open, "Get paid online" is already one of its
  // rows; a second banner for the same thing only adds clutter.
  const setup = useSetup();
  const guideOpen = setupInProgress(setup.data) && !setup.data.hidden;

  // Setting payments up is an admin's job (the server refuses everyone else),
  // so a member is not nagged to do something they cannot.
  if (!isAdmin || guideOpen) return null;

  // Nothing until the status is known. A banner that appears a beat after the
  // page has settled shoves the content down under the reader's eye.
  if (!data) return null;

  // Connect off for this server entirely, or the firm turned the nudge off.
  if (!data.enabled || !data.settings.showSetupBanner) return null;

  // Already taking payments. Onboarded-but-restricted still counts as not
  // ready, and gets different words below.
  const ready = data.connected && data.chargesEnabled;
  if (ready) return null;

  // Redundant on the settings screen: the card that does this is a scroll away,
  // and the switch that hides the banner is inside it.
  if (pathname === '/invoice-settings') return null;

  const unfinished = data.connected && !data.chargesEnabled;

  return (
    <div className="border-b border-[var(--line)] bg-[var(--accent-soft)]">
      {/*
        Full width with the page's own horizontal padding, not a centered
        content column. Pages pick between two max-widths (max-w-6xl, or
        max-w-[96rem] when `wide`), so a fixed width here would misalign with
        one of them; anchoring to the viewport reads as chrome instead, which
        is what it is.
      */}
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3">
        <CreditCard className="size-4 shrink-0 text-[var(--accent)]" aria-hidden />
        <p className="min-w-0 flex-1 text-sm">
          <span className="font-medium">
            {unfinished ? 'Finish setting up payments' : 'Start accepting payments online'}
          </span>
          <span className="text-muted">
            {unfinished
              ? ' — Stripe still needs a few details before your clients can pay.'
              : ' — let clients pay an invoice by card or bank transfer.'}
          </span>
        </p>
        <Button asChild variant="primary" size="sm" className="shrink-0">
          <Link to="/invoice-settings">
            {unfinished ? 'Finish setup' : 'Set up payments'}
          </Link>
        </Button>
      </div>
    </div>
  );
}
